import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import onboardingService from '../services/onboardingService';
import { getTourById } from '../walkthroughs';

/**
 * WalkthroughContext - global state for the interactive walkthrough engine.
 *
 * Tours are launched manually from the Hamster Training tab. There is no
 * auto-prompt on login: users discover tours via the training module.
 *
 * API:
 *   activeTour                  - the currently running tour object, or null
 *   activeTourId                - convenience getter
 *   startTour(tourId)           - begin a tour by id
 *   stopTour()                  - cancel the active tour without persisting
 *                                 completion (used for skip/close)
 *   completeTour()              - finish the active tour and stamp
 *                                 onboarding_completed_at on the backend
 */
const WalkthroughContext = createContext(null);

export const WalkthroughProvider = ({ children }) => {
    const [activeTourId, setActiveTourId] = useState(null);

    const activeTour = useMemo(
        () => (activeTourId ? getTourById(activeTourId) : null),
        [activeTourId]
    );

    const startTour = useCallback((tourId) => {
        const tour = getTourById(tourId);
        if (!tour) {
            // eslint-disable-next-line no-console
            console.warn(`[Walkthrough] Unknown tour id: ${tourId}`);
            return;
        }
        setActiveTourId(tourId);
    }, []);

    const stopTour = useCallback(() => {
        setActiveTourId(null);
    }, []);

    const completeTour = useCallback(async () => {
        setActiveTourId(null);
        try {
            await onboardingService.markComplete();
        } catch (err) {
            // Persistence failure is non-fatal: the tour still ran. Log so a
            // transient API issue does not block the user.
            // eslint-disable-next-line no-console
            console.error('[Walkthrough] Failed to persist completion:', err);
        }
    }, []);

    const value = useMemo(
        () => ({
            activeTour,
            activeTourId,
            startTour,
            stopTour,
            completeTour,
        }),
        [activeTour, activeTourId, startTour, stopTour, completeTour]
    );

    return (
        <WalkthroughContext.Provider value={value}>{children}</WalkthroughContext.Provider>
    );
};

export const useWalkthrough = () => {
    const ctx = useContext(WalkthroughContext);
    if (!ctx) {
        throw new Error('useWalkthrough must be used within a WalkthroughProvider');
    }
    return ctx;
};

export default WalkthroughContext;
